import SpotlightSvg from '@/assets/svgs/spotlight.svg';
import { P2PRateplan } from '@/containers/Accounts/types';
import DetailsCard from '@/shared/components/DetailsCard';
import { SpotlightBreakdownChart } from './SpotlightBreakdownChart';

interface RateplanBreakdownItem {
  label: string;
  count: number;
}

interface SpotlightCardProps {
  rateplanSavingsPerc?: number;
  rateplanSavingsTotalCountPerc?: number;
  rateplanBreakdown?: RateplanBreakdownItem[];
  p2pRateplan?: P2PRateplan;
}

export const SpotlightCard = ({
  rateplanSavingsPerc,
  rateplanSavingsTotalCountPerc,
  rateplanBreakdown,
  p2pRateplan,
}: SpotlightCardProps) => {
  return (
    <div className="mb-6">
      <DetailsCard icon={<SpotlightSvg className="h-5 w-5" />} title="Spotlight Savings">
        <div className="flex flex-col gap-4 md:flex-row">
          {/* Left side - Text content */}
          <div className="flex flex-1 flex-col gap-4">
            {/* Main savings percentage - left aligned */}
            <div>
              <div className="text-successful-600 text-4xl font-bold">
                {rateplanSavingsPerc !== undefined
                  ? `${Math.round(rateplanSavingsPerc)}%`
                  : '-'}
              </div>
              <div className="mt-1 text-sm font-bold text-gray-600">
                Savings Using Spotlight!
              </div>
            </div>

            {/* Coverage paragraph with rateplan breakdown */}
            {rateplanBreakdown &&
              rateplanBreakdown.length > 0 &&
              rateplanSavingsTotalCountPerc !== undefined && (
                <>
                  <div className="text-sm text-gray-700 dark:text-gray-500">
                    {(() => {
                      const spotlightBreakdown = rateplanBreakdown.filter(
                        (item) => item.label !== p2pRateplan?.default_label
                      );
                      const labels = spotlightBreakdown.map((item) =>
                        item.label.toLowerCase()
                      );
                      const labelsText =
                        labels.length === 1
                          ? labels[0]
                          : labels.length === 2
                            ? `${labels[0]} & ${labels[1]}`
                            : labels.slice(0, -1).join(', ') +
                              ' & ' +
                              labels[labels.length - 1];

                      return (
                        <p>
                          <strong>{Math.round(rateplanSavingsTotalCountPerc)}%</strong> of
                          your list includes both <i>{labelsText}</i> cost contacts.
                          Spotlight just slashed your rate on these contacts to:
                        </p>
                      );
                    })()}
                  </div>

                  {/* Rate ranges */}
                  {p2pRateplan?.rates && p2pRateplan.rates.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-gray-500">MMS Rate:</span>
                        <span className="text-successful-600 ml-2 font-mono font-medium">
                          {(() => {
                            const rates = p2pRateplan.rates;
                            const mmsRates = rates.map((r) => r.per_mms_success * 100);
                            const minRate = Math.min(...mmsRates);
                            const maxRate = Math.max(...mmsRates);
                            return `${minRate.toFixed(1)}¢ - ${maxRate.toFixed(1)}¢ per message`;
                          })()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">SMS Rate:</span>
                        <span className="text-successful-600 ml-2 font-mono font-medium">
                          {(() => {
                            const rates = p2pRateplan.rates;
                            const smsRates = rates.map((r) => r.per_sms_success * 100);
                            const minRate = Math.min(...smsRates);
                            const maxRate = Math.max(...smsRates);
                            return `${minRate.toFixed(1)}¢ - ${maxRate.toFixed(1)}¢ per message`;
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
          </div>

          {/* Right side - Donut Chart */}
          {rateplanBreakdown && rateplanBreakdown.length > 0 && (
            <SpotlightBreakdownChart
              rateplanBreakdown={rateplanBreakdown}
              defaultLabel={p2pRateplan?.default_label}
            />
          )}
        </div>
      </DetailsCard>
    </div>
  );
};
